import { Link, useLocation, useNavigate } from 'react-router-dom'
import { useState, useEffect } from 'react'
import { useQuery } from 'react-query'
import { useCart } from '../contexts/CartContext'
import { useCatalogSettings } from '../contexts/CatalogContext'
import ProductImage from '../components/ProductImage'
import { Package, Euro, Calendar, User, Phone, Mail, Building, CreditCard, Truck, FileText, CheckCircle, X, StickyNote } from 'lucide-react'
import { apiService as api } from '../services/api'
import { formatCurrency } from '../utils/currency'
import apiService from '../services/api'
import toast from 'react-hot-toast'

// Fonction pour détecter les jours fériés français et Guadeloupe
const getFrenchHolidays = (year) => {
  const holidays = []
  
  // Jours fériés fixes (France métropolitaine et Guadeloupe)
  holidays.push(new Date(year, 0, 1))   // Jour de l'An
  holidays.push(new Date(year, 4, 1))   // Fête du Travail
  holidays.push(new Date(year, 4, 8))   // Victoire 1945
  holidays.push(new Date(year, 4, 27))  // Abolition de l'esclavage (Guadeloupe)
  holidays.push(new Date(year, 6, 14))  // Fête Nationale
  holidays.push(new Date(year, 7, 15))  // Assomption
  holidays.push(new Date(year, 10, 1))  // Toussaint
  holidays.push(new Date(year, 10, 2))  // Jour des Défunts (Guadeloupe)
  holidays.push(new Date(year, 10, 11)) // Armistice 1918
  holidays.push(new Date(year, 11, 25)) // Noël
  
  // Pâques et jours liés (calculés)
  const easter = calculateEaster(year)
  holidays.push(easter) // Dimanche de Pâques
  
  // Carnaval et jours liés (Guadeloupe)
  // Mardi gras = 47 jours avant Pâques
  holidays.push(new Date(easter.getTime() - 47 * 24 * 60 * 60 * 1000)) // Mardi gras
  // Mercredi des Cendres = 46 jours avant Pâques
  holidays.push(new Date(easter.getTime() - 46 * 24 * 60 * 60 * 1000)) // Mercredi des Cendres
  // Mi-Carême = 24 jours avant Pâques
  holidays.push(new Date(easter.getTime() - 24 * 24 * 60 * 60 * 1000)) // Mi-Carême
  // Vendredi Saint = 2 jours avant Pâques
  holidays.push(new Date(easter.getTime() - 2 * 24 * 60 * 60 * 1000)) // Vendredi Saint
  
  // Lundi de Pâques = 1 jour après Pâques
  holidays.push(new Date(easter.getTime() + 24 * 60 * 60 * 1000)) // Lundi de Pâques
  
  // Ascension = 39 jours après Pâques
  holidays.push(new Date(easter.getTime() + 39 * 24 * 60 * 60 * 1000))
  
  // Lundi de Pentecôte = 50 jours après Pâques
  holidays.push(new Date(easter.getTime() + 50 * 24 * 60 * 60 * 1000))
  
  return holidays
}

// Fonction pour calculer Pâques
const calculateEaster = (year) => {
  const a = year % 19
  const b = Math.floor(year / 100)
  const c = year % 100
  const d = Math.floor(b / 4)
  const e = b % 4
  const f = Math.floor((b + 8) / 25)
  const g = Math.floor((b - f + 1) / 3)
  const h = (19 * a + b - d - g + 15) % 30
  const i = Math.floor(c / 4)
  const k = c % 4
  const l = (32 + 2 * e + 2 * i - h - k) % 7
  const m = Math.floor((a + 11 * h + 22 * l) / 451)
  const n = Math.floor((h + l - 7 * m + 114) / 31)
  const p = (h + l - 7 * m + 114) % 31
  
  return new Date(year, n - 1, p + 1)
}

// Fonction pour obtenir le nom d'un jour férié
const getHolidayName = (date) => {
  const year = date.getFullYear()
  const holidays = getFrenchHolidays(year)
  
  for (const holiday of holidays) {
    if (holiday.getDate() === date.getDate() && 
        holiday.getMonth() === date.getMonth() && 
        holiday.getFullYear() === date.getFullYear()) {
      
      const month = date.getMonth()
      const day = date.getDate()
      
      // Jours fériés fixes
      if (month === 0 && day === 1) return "Jour de l'An"
      if (month === 4 && day === 1) return "Fête du Travail"
      if (month === 4 && day === 8) return "Victoire 1945"
      if (month === 4 && day === 27) return "Abolition de l'esclavage"
      if (month === 6 && day === 14) return "Fête Nationale"
      if (month === 7 && day === 15) return "Assomption"
      if (month === 10 && day === 1) return "Toussaint"
      if (month === 10 && day === 2) return "Jour des Défunts"
      if (month === 10 && day === 11) return "Armistice 1918"
      if (month === 11 && day === 25) return "Noël"
      
      // Jours fériés mobiles (calculés à partir de Pâques)
      const easter = calculateEaster(year)
      const mardiGras = new Date(easter.getTime() - 47 * 24 * 60 * 60 * 1000)
      const mercrediCendres = new Date(easter.getTime() - 46 * 24 * 60 * 60 * 1000)
      const miCareme = new Date(easter.getTime() - 24 * 24 * 60 * 60 * 1000)
      const vendrediSaint = new Date(easter.getTime() - 2 * 24 * 60 * 60 * 1000)
      const easterMonday = new Date(easter.getTime() + 24 * 60 * 60 * 1000)
      const ascension = new Date(easter.getTime() + 39 * 24 * 60 * 60 * 1000)
      const pentecostMonday = new Date(easter.getTime() + 50 * 24 * 60 * 60 * 1000)
      
      if (date.getTime() === mardiGras.getTime()) return "Mardi gras"
      if (date.getTime() === mercrediCendres.getTime()) return "Mercredi des Cendres"
      if (date.getTime() === miCareme.getTime()) return "Mi-Carême"
      if (date.getTime() === vendrediSaint.getTime()) return "Vendredi Saint"
      if (date.getTime() === easter.getTime()) return "Dimanche de Pâques"
      if (date.getTime() === easterMonday.getTime()) return "Lundi de Pâques"
      if (date.getTime() === ascension.getTime()) return "Ascension"
      if (date.getTime() === pentecostMonday.getTime()) return "Lundi de Pentecôte"
    }
  }
  
  return null
}

// Fonction pour vérifier si une date est un jour férié
const isHoliday = (date) => {
  return getHolidayName(date) !== null
}

// Fonction pour calculer la date de récupération du matériel
const calculatePickupDate = (startDate) => {
  if (!startDate) return null
  
  // Créer une copie de la date à minuit pour éviter les problèmes de timezone
  const start = new Date(startDate)
  start.setHours(0, 0, 0, 0)
  
  const pickupDate = new Date(start)
  pickupDate.setDate(pickupDate.getDate() - 1) // 1 jour avant
  
  // Gérer les week-ends : si le retrait tombe un samedi ou dimanche, on recule au vendredi
  const dayOfWeek = pickupDate.getDay()
  if (dayOfWeek === 0) { // Dimanche → Vendredi (recule de 2 jours)
    pickupDate.setDate(pickupDate.getDate() - 2)
  } else if (dayOfWeek === 6) { // Samedi → Vendredi (recule de 1 jour)
    pickupDate.setDate(pickupDate.getDate() - 1)
  }
  
  return pickupDate
}

// Fonction pour calculer la date de retour du matériel
const calculateReturnDate = (endDate) => {
  if (!endDate) return null
  
  // Créer une copie de la date à minuit pour éviter les problèmes de timezone
  const end = new Date(endDate)
  end.setHours(0, 0, 0, 0)
  
  const returnDate = new Date(end)
  returnDate.setDate(returnDate.getDate() + 1) // 1 jour après
  
  // Gérer les week-ends : si le retour tombe un samedi ou dimanche, on avance au lundi
  const dayOfWeek = returnDate.getDay()
  if (dayOfWeek === 6) { // Samedi → Lundi (avance de 2 jours)
    returnDate.setDate(returnDate.getDate() + 2)
  } else if (dayOfWeek === 0) { // Dimanche → Lundi (avance de 1 jour)
    returnDate.setDate(returnDate.getDate() + 1)
  }
  
  return returnDate
}

// Fonction pour vérifier si une date est aujourd'hui
const isToday = (date) => {
  if (!date) return false
  const today = new Date()
  today.setHours(0, 0, 0, 0)
  const checkDate = new Date(date)
  checkDate.setHours(0, 0, 0, 0)
  return checkDate.getTime() === today.getTime()
}

// Fonction pour calculer les jours de location en excluant les dimanches
const calculateRentalDays = (startDate, endDate) => {
  if (!startDate || !endDate) return 0
  
  const start = new Date(startDate)
  const end = new Date(endDate)
  let days = 0
  const current = new Date(start)
  let weekendDays = 0
  
  // Parcourir chaque jour de la période
  while (current <= end) {
    const dayOfWeek = current.getDay()
    // Compter les jours de week-end (samedi = 6, dimanche = 0)
    if (dayOfWeek === 6 || dayOfWeek === 0) {
      weekendDays++
    } else {
      days++
    }
    current.setDate(current.getDate() + 1)
  }
  
  // Si on a des jours de week-end, on compte 1 jour pour tout le week-end
  if (weekendDays > 0) {
    days += 1
  }
  
  return Math.max(1, days) // Minimum 1 jour
}

const Checkout = () => {
  const { items, clearCart } = useCart()
  const { settings: catalogSettings } = useCatalogSettings()
  const location = useLocation()
  const navigate = useNavigate()
  const [startDate, setStartDate] = useState('')
  const [endDate, setEndDate] = useState('')
  const [customer, setCustomer] = useState(null)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [showAddressModal, setShowAddressModal] = useState(false)
  const [showConfirmationModal, setShowConfirmationModal] = useState(false)
  const [notes, setNotes] = useState('')
  const [addressData, setAddressData] = useState({
    street: '',
    street_number: '',
    city: '',
    postal_code: '',
    phone: ''
  })
  // Nombre de jours de location - Calcul en excluant les dimanches
  const rentalDays = (() => {
    try {
      if (!startDate || !endDate) return 0
      return calculateRentalDays(startDate, endDate)
    } catch {
      return 0
    }
  })()
  // Réglages publics (backend)
  const { data: adminSettingsData } = useQuery(
    'rentman-settings-public',
    () => api.get('/rentman/settings/public').then(res => res.data),
    { staleTime: 5 * 60 * 1000 }
  )
  const backendShowPrice = adminSettingsData?.show_regular_price
  const showPrice = backendShowPrice === true
  const { data: companyData } = useQuery('company-public', () => api.get('/rentman/company/public').then(r=>r.data), { staleTime: 5*60*1000 })
  const currency = companyData?.company?.currency || 'EUR'
  const currencyPos = companyData?.company?.currency_position || 'right'

  // Fonctions pour gérer le modal d'adresse
  const handleAddressChange = (field, value) => {
    setAddressData(prev => ({ ...prev, [field]: value }))
  }

  const handleSaveAddress = async () => {
    try {
      const me = await apiService.get('/auth/me', { params: { t: Date.now() } }).then(r => r.data?.user)
      if (!me?.id) {
        toast.error('Utilisateur non trouvé')
        return
      }

      const payload = {
        first_name: me.first_name,
        last_name: me.last_name,
        phone: me.phone,
        company_name: me.company_name,
        street: addressData.street,
        street_number: addressData.street_number,
        city: addressData.city,
        postal_code: addressData.postal_code,
      }

      await apiService.put('/users/me', payload)
      setShowAddressModal(false)
      toast.success('Informations mises à jour')
      
      // Relancer la procédure de checkout (qui affichera le modal de confirmation)
      setTimeout(() => {
        handleCheckout()
      }, 100)
    } catch (e) {
      console.error('Erreur lors de la mise à jour:', e)
      toast.error('Erreur lors de la mise à jour de l\'adresse')
    }
  }

  const handleCheckout = async () => {
    if (isSubmitting) return
    
    if (!startDate || !endDate) {
      toast.error('Merci de renseigner les dates')
      return
    }
    const isLogged = !!localStorage.getItem('user_token')
    const qs = location.search || ''
    if (!isLogged) {
      navigate(`/login?redirect=${encodeURIComponent('/checkout'+qs)}`)
      return
    }
    
    // Vérifier que l'adresse du client est complète
    let me
    try {
      me = await apiService.get('/auth/me', { params: { t: Date.now() } }).then(r => r.data?.user)
      if (!me?.id) {
        toast.error('Utilisateur non trouvé')
        return
      }
      
      // Vérifier les champs requis (adresse + téléphone)
      const requiredFields = ['street', 'city', 'postal_code', 'phone']
      const missingFields = requiredFields.filter(field => !me[field] || me[field].trim() === '')
      
      if (missingFields.length > 0) {
        // Pré-remplir le modal avec les données existantes
        setAddressData({
          street: me.street || '',
          street_number: me.street_number || '',
          city: me.city || '',
          postal_code: me.postal_code || '',
          phone: me.phone || ''
        })
        setShowAddressModal(true)
        return
      }
    } catch (e) {
      console.error('Erreur lors de la vérification du profil:', e)
      toast.error('Erreur lors de la vérification du profil')
      return
    }
    
    // Afficher le modal de confirmation
    setShowConfirmationModal(true)
  }

  const handleConfirmAndSubmit = async () => {
    setShowConfirmationModal(false)
    
    setIsSubmitting(true)
    try {
      // Récupérer les données utilisateur
      const me = await apiService.get('/auth/me', { params: { t: Date.now() } }).then(r => r.data?.user)
      
      // Créer toutes les réservations en une seule requête
      const payload = {
        client_id: me.id,
        items: items.map(item => ({ 
          id: item.id, 
          name: item.name, 
          quantity: item.quantity || 1,
          price_per_day: item.price_per_day || 0
        })),
        start_date: startDate,
        end_date: endDate,
        notes: notes || ''
      }
      
      const res = await apiService.post('/reservations/batch', payload)
      const createdReservation = res.data?.reservation
      
      if (createdReservation) {
        try {
          localStorage.removeItem('rental_start')
          localStorage.removeItem('rental_end')
        } catch {}
        clearCart()
        setStartDate('')
        setEndDate('')
        toast.success('Demande envoyée')
        navigate('/quote-confirmation')
      } else {
        toast.error('Aucune réservation créée')
      }
    } catch (e) {
      const msg = e?.response?.data?.message || e?.response?.data?.error || e?.message
      toast.error(msg || 'Erreur lors de la création de la réservation')
      console.error('Erreur réservation:', e?.response?.data || e)
    } finally {
      setIsSubmitting(false)
    }
  }

  // Hydrate dates from query params (one time)
  useEffect(() => {
    const params = new URLSearchParams(location.search)
    const startQ = params.get('start')
    const endQ = params.get('end')
    if (startQ) setStartDate(startQ)
    if (endQ) setEndDate(endQ)
  }, [location.search])

  // Load customer profile if logged (user_token)
  useEffect(() => {
    if (!customer && localStorage.getItem('user_token')) {
      apiService.get('/auth/me', { params: { t: Date.now() } })
        .then(res => setCustomer(res.data?.user || null))
        .catch(()=>{})
    }
  }, [customer])

  const formatDateFR = (d) => {
    if (!d) return ''
    // accepts 'YYYY-MM-DD'
    const [y, m, day] = d.split('-')
    if (y && m && day) return `${day}/${m}/${y}`
    return d
  }

  const formatDateFRFull = (d) => {
    if (!d) return ''
    const date = new Date(d)
    return date.toLocaleDateString('fr-FR', { 
      weekday: 'long', 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    })
  }

  const total = items.reduce((sum, p) => sum + (p.price_per_day || 0) * (p.quantity || 1) * (rentalDays || 0), 0)

  return (
    <div className="min-h-screen bg-gray-100">
      <div className="container-custom py-8">
        <div className="mb-8">
          <div className="flex items-center mb-2">
            <FileText className="h-8 w-8 text-orange-500 mr-3" />
            <h1 className="text-4xl font-bold">Confirmation avant devis</h1>
          </div>
          <p className="text-gray-600 ml-11">Finaliser votre demande de devis</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Récap liste */}
          <div className="lg:col-span-2 space-y-4">
            <div className="card">
              <h2 className="text-xl font-semibold mb-4 flex items-center">
                <Package className="h-5 w-5 text-orange-500 mr-2" />
                Votre liste d'équipement
              </h2>
              {items.length === 0 ? (
                <div className="text-center py-8">
                  <Package className="h-10 w-10 text-gray-400 mx-auto mb-3" />
                  <p className="text-gray-600">Aucun article dans la liste</p>
                  <Link to="/catalogue-materiels" className="btn-primary inline-flex items-center mt-4">Retour au catalogue</Link>
                </div>
              ) : (
                <div className="space-y-4">
                  {items.map(item => (
                    <div key={item.id} className="flex items-center gap-4">
                      <div className="h-16 w-16 bg-white rounded-lg overflow-hidden flex-shrink-0">
                        <ProductImage imageUrl={item.image_url} alt={item.name} className="w-full h-full object-cover" fallbackIcon={Package} fallbackIconSize="h-6 w-6" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium truncate">{item.name}</p>
                        <p className="text-sm text-gray-600">Qté: {item.quantity || 1}</p>
                      </div>
                      {showPrice && (
                        <div className="text-right">
                          <div className="flex items-center justify-end space-x-1">
                            <Euro className="h-4 w-4 text-orange-500" />
                            <span className="font-semibold">{formatCurrency((item.price_per_day || 0) * (item.quantity || 1) * (rentalDays || 0), currency, currencyPos)}</span>
                          </div>
                           <div className="text-xs text-gray-500">{formatCurrency(item.price_per_day || 0, currency, currencyPos)}/jour</div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Période de location souhaitée */}
          <div>
            <div className="card">
              <h2 className="text-xl font-semibold mb-4 flex items-center">
                <Calendar className="h-5 w-5 text-orange-500 mr-2" />
                Date de tournage prévue
              </h2>

              <div className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Date de départ</label>
                    <input type="text" className="input-field" value={formatDateFR(startDate)} readOnly />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Date de retour</label>
                    <input type="text" className="input-field" value={formatDateFR(endDate)} readOnly />
                  </div>
                </div>
                {startDate && endDate && (
                  <div className="text-sm text-gray-700 pt-2">
                    {(() => {
                      // Convertir les strings YYYY-MM-DD en Date (même logique que Panier)
                      // Important: créer la date à minuit local pour éviter les problèmes de timezone
                      const parseDate = (dateStr) => {
                        if (!dateStr) return null
                        const [year, month, day] = dateStr.split('-').map(Number)
                        return new Date(year, month - 1, day, 0, 0, 0, 0)
                      }
                      
                      const startDateObj = parseDate(startDate)
                      const endDateObj = parseDate(endDate)
                      
                      if (!startDateObj || !endDateObj) return null
                      
                      // Calculer les jours en excluant les dimanches (même logique que Panier)
                      const days = calculateRentalDays(startDateObj, endDateObj)
                      const startFormatted = startDateObj.toLocaleDateString('fr-FR', { 
                        weekday: 'long', 
                        year: 'numeric', 
                        month: 'long', 
                        day: 'numeric' 
                      })
                      const endFormatted = endDateObj.toLocaleDateString('fr-FR', { 
                        weekday: 'long', 
                        year: 'numeric', 
                        month: 'long', 
                        day: 'numeric' 
                      })
                      
                      // Calculer les dates de récupération et retour (même logique que Panier)
                      const pickupDate = calculatePickupDate(startDateObj)
                      const returnDate = calculateReturnDate(endDateObj)
                      const pickupFormatted = pickupDate ? pickupDate.toLocaleDateString('fr-FR', { 
                        weekday: 'long', 
                        year: 'numeric', 
                        month: 'long', 
                        day: 'numeric' 
                      }) : ''
                      const returnFormatted = returnDate ? returnDate.toLocaleDateString('fr-FR', { 
                        weekday: 'long', 
                        year: 'numeric', 
                        month: 'long', 
                        day: 'numeric' 
                      }) : ''
                      
                      return (
                        <div className="space-y-2">
                          <div className="space-y-1">
                            <div><strong>Durée:</strong> {days} jour{days > 1 ? 's' : ''}</div>
                            <div className="text-xs text-gray-600">
                              {days === 1 ? (
                                <div>{startFormatted}</div>
                              ) : (
                                <>
                                  <div>Du {startFormatted}</div>
                                  <div>Au {endFormatted}</div>
                                </>
                              )}
                            </div>
                          </div>
                          {(pickupFormatted || returnFormatted) && (
                            <div className="pt-2 border-t border-gray-200">
                              <div className="text-sm font-medium text-gray-700 mb-1">
                                <span>Info retrait :</span>
                              </div>
                              <div className="text-xs text-gray-600 space-y-1">
                                {pickupFormatted && (
                                  <div><strong>Départ :</strong> {isToday(startDateObj) ? (
                                    <>Le matériel sera à récupérer ce jour à partir de <u>15h</u> sous réserve de disponibilité du matériel.</>
                                  ) : (
                                    <>Le matériel sera à récupérer le <u>{pickupFormatted}</u> à partir de <u>15h</u></>
                                  )}</div>
                                )}
                                {returnFormatted && (
                                  <div><strong>Retour :</strong> Prévu le <u>{returnFormatted}</u> avant <u>11h</u></div>
                                )}
                              </div>
                              {/* Alerte pour les jours fériés */}
                              {(() => {
                                const parseDate = (dateStr) => {
                                  if (!dateStr) return null
                                  const [year, month, day] = dateStr.split('-').map(Number)
                                  return new Date(year, month - 1, day, 0, 0, 0, 0)
                                }
                                const startDateObj = parseDate(startDate)
                                const endDateObj = parseDate(endDate)
                                if (!startDateObj || !endDateObj) return null
                                const pickupDate = calculatePickupDate(startDateObj)
                                const returnDate = calculateReturnDate(endDateObj)
                                const pickupHoliday = pickupDate ? getHolidayName(pickupDate) : null
                                const returnHoliday = returnDate ? getHolidayName(returnDate) : null
                                
                                if (pickupHoliday || returnHoliday) {
                                  return (
                                    <div className="mt-2 p-2 bg-yellow-50 border border-yellow-200 rounded-md">
                                      <div className="text-xs text-yellow-800">
                                        <div className="font-medium mb-1">⚠️ Attention :</div>
                                        {pickupHoliday && (
                                          <div className="mb-1">
                                            Nous serons potentiellement fermés le <strong>{pickupDate.toLocaleDateString('fr-FR', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}</strong> car c'est <strong>{pickupHoliday}</strong>. Merci de nous contacter pour le retrait.
                                          </div>
                                        )}
                                        {returnHoliday && (
                                          <div>
                                            Nous serons potentiellement fermés le <strong>{returnDate.toLocaleDateString('fr-FR', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}</strong> car c'est <strong>{returnHoliday}</strong>. Merci de nous contacter pour le retour.
                                          </div>
                                        )}
                                      </div>
                                    </div>
                                  )
                                }
                                return null
                              })()}
                            </div>
                          )}
                        </div>
                      );
                    })()}
                  </div>
                )}
                <div className="pt-2 text-right">
                  <Link to="/panier" className="btn-outline inline-flex items-center">Modifier les dates</Link>
                </div>

                {showPrice && (
                  <div className="flex items-center justify-between pt-2">
                    <span>Total estimé</span>
                    <span className="font-semibold">{formatCurrency(total, currency, currencyPos)}</span>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Détails du devis, Paiement/Livraison & Notes */}
          <div className="lg:col-span-3">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-6">
              <div className="card">
                <h3 className="text-lg font-semibold mb-4 flex items-center"><User className="h-5 w-5 text-orange-500 mr-2" /> Détails du devis</h3>
                {customer ? (
                  <div className="space-y-2 text-sm">
                    <div className="flex items-center space-x-2"><User className="h-4 w-4 text-gray-600" /><span>{customer.first_name} {customer.last_name}</span></div>
                    <div className="flex items-center space-x-2"><Mail className="h-4 w-4 text-gray-600" /><span>{customer.email}</span></div>
                    {customer.phone && (<div className="flex items-center space-x-2"><Phone className="h-4 w-4 text-gray-600" /><span>{customer.phone}</span></div>)}
                    {customer.company_name && (<div className="flex items-center space-x-2"><Building className="h-4 w-4 text-gray-600" /><span>{customer.company_name}</span></div>)}
                    {(customer.street || customer.city || customer.postal_code) && (
                      <div className="text-gray-700">
                        <div className="text-xs text-gray-500">Adresse</div>
                        <div>{[customer.street, customer.street_number].filter(Boolean).join(' ')}</div>
                        <div>{[customer.postal_code, customer.city].filter(Boolean).join(' ')}</div>
                      </div>
                    )}
                    <div className="text-right">
                      <Link to={`/account?redirect=${encodeURIComponent(location.pathname + location.search)}`} className="btn-outline mt-2 inline-flex items-center">Modifier</Link>
                    </div>
                  </div>
                ) : (
                  <p className="text-sm text-gray-600">Connectez-vous pour voir vos informations de compte.</p>
                )}
              </div>
              <div className="card">
                <h3 className="text-lg font-semibold mb-4 flex items-center"><CreditCard className="h-5 w-5 text-orange-500 mr-2" /> Paiement & Livraison</h3>
                <div className="space-y-4 text-sm text-gray-600">
                  <div className="flex items-start space-x-3">
                    <CreditCard className="h-4 w-4 text-orange-500 mt-0.5 flex-shrink-0" />
                    <div>
                      Paiement à la livraison - détail sur devis
                    </div>
                  </div>
                  <div className="flex items-start space-x-3">
                    <Truck className="h-4 w-4 text-orange-500 mt-0.5 flex-shrink-0" />
                    <div>
                      Retrait dans nos locaux
                    </div>
                  </div>
                  <div className="text-xs text-gray-500 mt-2 p-3 bg-gray-50 rounded border-l-4 border-orange-200">
                    <strong>Adresse :</strong><br />
                    Immeuble Oiseau du Paradis ZI Jarry – 42 Rue Henri Becquerel 97122 Baie-Mahault
                  </div>
                </div>
              </div>
              <div className="card">
                <h3 className="text-lg font-semibold mb-4 flex items-center"><FileText className="h-5 w-5 text-orange-500 mr-2" /> Notes</h3>
                <div className="space-y-3">
                  <textarea
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    className="input-field resize-none"
                    rows={4}
                    placeholder="Ajoutez des notes ou commentaires pour votre demande..."
                  />
                  <p className="text-xs text-gray-500">
                    Ces notes seront transmises avec votre demande de devis.
                  </p>
                </div>
              </div>
            </div>
            <div className="mt-6 text-right">
              <button
                className={`btn-primary ${isSubmitting ? 'opacity-50 cursor-not-allowed' : ''}`}
                disabled={isSubmitting}
                onClick={handleCheckout}
              >
                Envoyer votre demande
              </button>
            </div>
          </div>
        </div>
      </div>
      
      {/* Overlay de chargement */}
      {isSubmitting && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-8 max-w-md mx-4 text-center shadow-2xl">
            <div className="flex flex-col items-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-4 border-orange-500 mb-4"></div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">
                Transfert de votre demande
              </h3>
              <p className="text-gray-600 mb-4">
                à l'équipe Flightcase...
              </p>
              <div className="flex space-x-1">
                <div className="w-2 h-2 bg-orange-500 rounded-full animate-bounce"></div>
                <div className="w-2 h-2 bg-orange-500 rounded-full animate-bounce" style={{animationDelay: '0.1s'}}></div>
                <div className="w-2 h-2 bg-orange-500 rounded-full animate-bounce" style={{animationDelay: '0.2s'}}></div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal de mise à jour d'adresse */}
      {showAddressModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md mx-4 w-full shadow-2xl">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">
                Complétez votre adresse
              </h3>
              <button
                onClick={() => setShowAddressModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            
            <p className="text-sm text-gray-600 mb-4">
              Veuillez compléter votre adresse et votre téléphone pour finaliser votre commande.
            </p>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Adresse</label>
                <div className="grid grid-cols-3 gap-2">
                  <input
                    className="input-field"
                    placeholder="N°"
                    value={addressData.street_number}
                    onChange={(e) => handleAddressChange('street_number', e.target.value)}
                  />
                  <input
                    className="input-field col-span-2"
                    placeholder="Rue"
                    value={addressData.street}
                    onChange={(e) => handleAddressChange('street', e.target.value)}
                  />
                </div>
              </div>
              
              <div>
                <div className="grid grid-cols-3 gap-2">
                  <input
                    className="input-field"
                    placeholder="Code postal"
                    value={addressData.postal_code}
                    onChange={(e) => handleAddressChange('postal_code', e.target.value)}
                  />
                  <input
                    className="input-field col-span-2"
                    placeholder="Ville"
                    value={addressData.city}
                    onChange={(e) => handleAddressChange('city', e.target.value)}
                  />
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Téléphone <span className="text-red-500">*</span></label>
                <input
                  type="tel"
                  className="input-field"
                  placeholder="06 12 34 56 78"
                  value={addressData.phone}
                  onChange={(e) => handleAddressChange('phone', e.target.value)}
                />
              </div>
            </div>

            <div className="flex justify-end space-x-3 mt-6">
              <button
                onClick={() => setShowAddressModal(false)}
                className="btn-outline"
              >
                Annuler
              </button>
              <button
                onClick={handleSaveAddress}
                className="btn-primary"
                disabled={!addressData.street || !addressData.city || !addressData.postal_code || !addressData.phone}
              >
                Enregistrer et continuer
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de confirmation avant envoi */}
      {showConfirmationModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-md w-full shadow-2xl">
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                  <CheckCircle className="h-5 w-5 text-green-500 mr-2" />
                  Confirmer votre demande
                </h3>
                <button
                  onClick={() => setShowConfirmationModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
              
              <div className="mb-6">
                <p className="text-sm text-gray-600 mb-4">
                  Voulez-vous vraiment envoyer cette demande de devis ?
                </p>
                
                {/* Récapitulatif des dates */}
                <div className="bg-gray-50 rounded-lg p-4 mb-4">
                  <h4 className="font-medium text-gray-900 mb-2 flex items-center">
                    <Calendar className="h-4 w-4 text-orange-500 mr-2" />
                    Date de tournage prévue
                  </h4>
                  <div className="space-y-1 text-sm text-gray-700">
                    {rentalDays === 1 ? (
                      <div>{formatDateFRFull(startDate)}</div>
                    ) : (
                      <>
                        <div><strong>Du :</strong> {formatDateFR(startDate)}</div>
                        <div><strong>Au :</strong> {formatDateFR(endDate)}</div>
                      </>
                    )}
                    <div className="pt-2 border-t border-gray-200">
                      <strong>Durée :</strong> {rentalDays} jour{rentalDays > 1 ? 's' : ''}
                    </div>
                    {(() => {
                      // Convertir les strings YYYY-MM-DD en Date (même logique que Panier)
                      const parseDate = (dateStr) => {
                        if (!dateStr) return null
                        const [year, month, day] = dateStr.split('-').map(Number)
                        return new Date(year, month - 1, day, 0, 0, 0, 0)
                      }
                      
                      const startDateObj = parseDate(startDate)
                      const endDateObj = parseDate(endDate)
                      if (!startDateObj || !endDateObj) return null
                      
                      const pickupDate = calculatePickupDate(startDateObj)
                      const returnDate = calculateReturnDate(endDateObj)
                      const pickupFormatted = pickupDate ? pickupDate.toLocaleDateString('fr-FR', { 
                        weekday: 'long', 
                        year: 'numeric', 
                        month: 'long', 
                        day: 'numeric' 
                      }) : ''
                      const returnFormatted = returnDate ? returnDate.toLocaleDateString('fr-FR', { 
                        weekday: 'long', 
                        year: 'numeric', 
                        month: 'long', 
                        day: 'numeric' 
                      }) : ''
                      return (pickupFormatted || returnFormatted) ? (
                        <div className="pt-2 border-t border-gray-200 mt-2">
                          <div className="text-sm font-medium text-gray-700 mb-1">
                            <span>Info retrait :</span>
                          </div>
                          <div className="text-xs text-gray-600 space-y-1">
                            {pickupFormatted && (
                              <div><strong>Départ :</strong> {isToday(startDateObj) ? (
                                <>Le matériel sera à récupérer ce jour à partir de <u>15h</u> sous réserve de disponibilité du matériel.</>
                              ) : (
                                <>Le matériel sera à récupérer le <u>{pickupFormatted}</u> à partir de <u>15h</u></>
                              )}</div>
                            )}
                            {returnFormatted && (
                              <div><strong>Retour :</strong> Prévu le <u>{returnFormatted}</u> avant <u>11h</u></div>
                            )}
                          </div>
                          {/* Alerte pour les jours fériés */}
                          {(() => {
                            const parseDate = (dateStr) => {
                              if (!dateStr) return null
                              const [year, month, day] = dateStr.split('-').map(Number)
                              return new Date(year, month - 1, day, 0, 0, 0, 0)
                            }
                            const startDateObj = parseDate(startDate)
                            const endDateObj = parseDate(endDate)
                            if (!startDateObj || !endDateObj) return null
                            const pickupDate = calculatePickupDate(startDateObj)
                            const returnDate = calculateReturnDate(endDateObj)
                            const pickupHoliday = pickupDate ? getHolidayName(pickupDate) : null
                            const returnHoliday = returnDate ? getHolidayName(returnDate) : null
                            
                            if (pickupHoliday || returnHoliday) {
                              return (
                                <div className="mt-2 p-2 bg-yellow-50 border border-yellow-200 rounded-md">
                                  <div className="text-xs text-yellow-800">
                                    <div className="font-medium mb-1">⚠️ Attention :</div>
                                    {pickupHoliday && (
                                      <div className="mb-1">
                                        Nous serons potentiellement fermés le <strong>{pickupDate.toLocaleDateString('fr-FR', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}</strong> car c'est <strong>{pickupHoliday}</strong>. Merci de nous contacter pour le retrait.
                                      </div>
                                    )}
                                    {returnHoliday && (
                                      <div>
                                        Nous serons potentiellement fermés le <strong>{returnDate.toLocaleDateString('fr-FR', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}</strong> car c'est <strong>{returnHoliday}</strong>. Merci de nous contacter pour le retour.
                                      </div>
                                    )}
                                  </div>
                                </div>
                              )
                            }
                            return null
                          })()}
                        </div>
                      ) : null
                    })()}
                  </div>
                </div>

                {/* Récapitulatif des équipements */}
                <div className="bg-gray-50 rounded-lg p-4 mb-4">
                  <h4 className="font-medium text-gray-900 mb-2 flex items-center">
                    <Package className="h-4 w-4 text-orange-500 mr-2" />
                    Équipements ({items.length})
                  </h4>
                  <div className="space-y-1 text-sm text-gray-700 max-h-32 overflow-y-auto">
                    {items.map((item, index) => (
                      <div key={index} className="flex justify-between">
                        <span className="truncate flex-1 mr-2">{item.name}</span>
                        <span className="text-gray-500">x{item.quantity || 1}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Notes si présentes */}
                {notes && (
                  <div className="bg-blue-50 rounded-lg p-4">
                    <h4 className="font-medium text-gray-900 mb-2 flex items-center">
                      <FileText className="h-4 w-4 text-blue-500 mr-2" />
                      Notes
                    </h4>
                    <p className="text-sm text-gray-700">{notes}</p>
                  </div>
                )}
              </div>

              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => setShowConfirmationModal(false)}
                  className="btn-outline"
                >
                  Annuler
                </button>
                <button
                  onClick={handleConfirmAndSubmit}
                  className="btn-primary flex items-center"
                >
                  <CheckCircle className="h-4 w-4 mr-2" />
                  Confirmer et envoyer
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default Checkout


